import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createStyles } from '@/contexts/theme-context';
import { Icons } from '@usepepper/sprout-kit-ui';
import {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  Pressable,
  ScrollView,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';

const DEFAULT_SCROLL_DISTANCE = 200;
const EDGE_EPSILON = 1;
// Enlarges the click target beyond the small chevron without affecting layout.
const ARROW_HIT_SLOP = { bottom: 8, left: 8, right: 8, top: 8 };

interface Props {
  /** Announced on the back (left) arrow. */
  backAccessibilityLabel: string;
  children: React.ReactNode;
  /** Style for the scrolled content row (passed to the ScrollView). */
  contentContainerStyle?: StyleProp<ViewStyle>;
  /** Announced on the forward (right) arrow. */
  forwardAccessibilityLabel: string;
  /** Fires whenever the scrolled content resizes. */
  onContentSizeChange?: () => void;
  /** Pixels each arrow click scrolls. Defaults to 200. */
  scrollDistance?: number;
  /** Style for the outer wrapper that positions the arrows. */
  style?: StyleProp<ViewStyle>;
}

export interface HorizontalScrollWithArrowsHandle {
  /** Jumps the row fully to the end without animation. */
  scrollToEnd: () => void;
}

/**
 * Horizontally scrolling row with web-only arrows flanking the scroll area, for
 * pointer users who can't scroll horizontally without a trackpad.
 */
export const HorizontalScrollWithArrows = forwardRef<
  HorizontalScrollWithArrowsHandle,
  Props
>(function HorizontalScrollWithArrows(
  {
    backAccessibilityLabel,
    children,
    contentContainerStyle,
    forwardAccessibilityLabel,
    onContentSizeChange,
    scrollDistance = DEFAULT_SCROLL_DISTANCE,
    style,
  },
  ref,
) {
  const [styles, theme] = useStyles();
  const scrollRef = useRef<ScrollView>(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [layoutWidth, setLayoutWidth] = useState(0);
  const [scrollOffset, setScrollOffset] = useState(0);

  // Lets a consumer chase a trailing item that grows after it renders into view
  useImperativeHandle(ref, () => ({
    scrollToEnd: () => scrollRef.current?.scrollToEnd({ animated: false }),
  }));

  const isWeb = Platform.OS === 'web';
  const showArrows = isWeb && contentWidth > layoutWidth;
  const canScrollBack = scrollOffset > EDGE_EPSILON;
  const canScrollForward =
    contentWidth - layoutWidth - scrollOffset > EDGE_EPSILON;

  const scrollBy = (delta: number) => {
    scrollRef.current?.scrollTo({ animated: true, x: scrollOffset + delta });
  };

  const chevronColor = (disabled: boolean, hovered?: boolean) =>
    disabled
      ? theme.sk.color.icon.disabled
      : hovered
        ? theme.sk.color.icon.secondary
        : theme.sk.color.icon.primary;

  return (
    <View style={[styles.container, style]}>
      {showArrows ? (
        <Pressable
          accessibilityLabel={backAccessibilityLabel}
          accessibilityRole="button"
          disabled={!canScrollBack}
          hitSlop={ARROW_HIT_SLOP}
          onPress={() => scrollBy(-scrollDistance)}
          style={styles.arrowLeft}
        >
          {({ hovered }) => (
            <Icons.ChevronLeft
              color={chevronColor(!canScrollBack, hovered)}
              size={theme.sk.spacing.spaceMedium}
            />
          )}
        </Pressable>
      ) : null}
      <ScrollView
        contentContainerStyle={contentContainerStyle}
        horizontal
        onContentSizeChange={(width) => {
          if (isWeb) {
            setContentWidth(width);
          }
          onContentSizeChange?.();
        }}
        onLayout={(event: LayoutChangeEvent) => {
          if (isWeb) {
            setLayoutWidth(event.nativeEvent.layout.width);
          }
        }}
        onScroll={(event: NativeSyntheticEvent<NativeScrollEvent>) => {
          if (!isWeb) {
            return;
          }
          // One snapshot so the end-of-scroll check reconciles exactly.
          const { contentOffset, contentSize, layoutMeasurement } =
            event.nativeEvent;
          setScrollOffset(contentOffset.x);
          setContentWidth(contentSize.width);
          setLayoutWidth(layoutMeasurement.width);
        }}
        ref={scrollRef}
        scrollEventThrottle={16}
        showsHorizontalScrollIndicator={false}
        style={styles.scrollView}
      >
        {children}
      </ScrollView>
      {showArrows ? (
        <Pressable
          accessibilityLabel={forwardAccessibilityLabel}
          accessibilityRole="button"
          disabled={!canScrollForward}
          hitSlop={ARROW_HIT_SLOP}
          onPress={() => scrollBy(scrollDistance)}
          style={styles.arrowRight}
        >
          {({ hovered }) => (
            <Icons.ChevronRight
              color={chevronColor(!canScrollForward, hovered)}
              size={theme.sk.spacing.spaceMedium}
            />
          )}
        </Pressable>
      ) : null}
    </View>
  );
});

const useStyles = createStyles(() => ({
  // Pull the chevron flush, cancelling its viewBox whitespace.
  arrowLeft: {
    marginLeft: -2,
  },
  arrowRight: {
    marginRight: -2,
  },
  container: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  scrollView: {
    flex: 1,
  },
}));
